/**
 * Data hooks for the CSV lead-import wizard — docs/modules/leadImport.model.md.
 * `useImportJob` is the first "enqueue and poll" pattern in this frontend
 * (the email-send queue never grew one — it just toasts and moves on); it
 * polls `GET /imports/{id}` every 1.5s while `status` is `running`/`pending`/
 * `validating`, and stops once the job reaches a terminal state.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as filesApi from '../../api/files.api.js';
import * as importsApi from '../../api/imports.api.js';
import { pushToast } from '../ui/uiSlice.js';

const MAX_CSV_BYTES = 10 * 1024 * 1024;
const TERMINAL_STATUSES = ['completed', 'failed', 'rolled_back'];

async function sha256Hex(file) {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Upload the CSV through the same presigned pair Documents uses, `purpose: 'general'` — an import source isn't attached to a record. */
export function useUploadCsv() {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (file) => {
      if (file.size > MAX_CSV_BYTES) throw new Error('That file is larger than the 10 MB limit.');
      const checksum = await sha256Hex(file);
      const { object_id: objectId, upload } = await filesApi.createUploadUrl({
        filename: file.name, content_type: file.type || 'text/csv', byte_size: file.size,
        checksum_sha256: checksum, purpose: 'general',
      });
      const putResponse = await filesApi.uploadToPresignedUrl(upload, file);
      const etag = putResponse.headers.etag ?? putResponse.headers.ETag;
      await filesApi.commitUpload(objectId, { etag, purpose: 'general' });
      return objectId;
    },
    onError: (error) => dispatch(pushToast({ tone: 'danger', message: 'Could not upload that file', detail: error.message })),
  });
}

export function useCreateImport() {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: (body) => importsApi.createImport(body),
    onError: (error) => dispatch(pushToast({ tone: 'danger', message: 'Could not start the import', detail: error.message })),
  });
}

export function useValidateImport() {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: ({ id, body }) => importsApi.validateImport(id, body),
    onError: (error) => dispatch(pushToast({ tone: 'danger', message: 'Could not read that CSV', detail: error.message })),
  });
}

export function useExecuteImport() {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: ({ id, body }) => importsApi.executeImport(id, body),
    onError: (error) => dispatch(pushToast({ tone: 'danger', message: 'Could not start the import run', detail: error.message })),
  });
}

/** Polls while the job is in flight; stops on its own once status is terminal. */
export function useImportJob(id, { enabled = true } = {}) {
  return useQuery({
    queryKey: ['imports', id],
    queryFn: () => importsApi.getImport(id),
    enabled: Boolean(id) && enabled,
    refetchInterval: (query) => (TERMINAL_STATUSES.includes(query.state.data?.status) ? false : 1500),
  });
}
